var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useIntl, FormattedMessage, FormattedDate } from "react-intl";
import { Modal, ModalFooter, Button, Loading, Accordion, AccordionSet, } from "@folio/stripes/components";
import { RecordHistory } from "../enforcement/RecordHistory";
import { StatusBadgeList } from "../common/StatusBadgeList";
import { LibraryReference } from "../library/LibraryReference";
import { useCrossLibraryHistory } from "../../hooks/useCrossLibraryHistory";
import styles from "./CrossLibraryHistoryModal.module.css";
var RecordRow = function (_a) {
    var record = _a.record, parentOpen = _a.parentOpen;
    var _b = useState(false), showTimeline = _b[0], setShowTimeline = _b[1];
    useEffect(function () {
        if (!parentOpen) {
            setShowTimeline(false);
        }
    }, [parentOpen]);
    var handleToggle = useCallback(function () { return setShowTimeline(function (v) { return !v; }); }, []);
    var statusesPerRecord = useMemo(function () { return [[record.status]]; }, [record.status]);
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.recordSummary },
            React.createElement("span", null,
                React.createElement(FormattedMessage, { id: record.type === "REQUESTED"
                        ? "ui-br-legaldeposits.detail.volume.label.copy"
                        : "ui-br-legaldeposits.detail.volume.label", values: { number: record.copyNumber } }),
                record.extraCopyReason && (React.createElement(React.Fragment, null,
                    " - ",
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.detail.volume.extraReason.".concat(record.extraCopyReason) })))),
            React.createElement(StatusBadgeList, { statusesPerRecord: statusesPerRecord }),
            React.createElement(Button, { buttonStyle: "link", onClick: handleToggle },
                React.createElement(FormattedMessage, { id: showTimeline
                        ? "ui-br-legaldeposits.title.crossLibraryHistory.hideTimeline"
                        : "ui-br-legaldeposits.title.crossLibraryHistory.showTimeline" }))),
        showTimeline && React.createElement(RecordHistory, { recordId: record.id })));
};
var LibraryAccordion = function (_a) {
    var group = _a.group, open = _a.open, onToggle = _a.onToggle;
    var accordionLabel = useMemo(function () { return (React.createElement("span", { className: styles.libraryHeader },
        React.createElement("span", { className: styles.libraryName },
            React.createElement(LibraryReference, { name: group.library.name, status: group.library.status })),
        React.createElement(StatusBadgeList, { statusesPerRecord: group.records.map(function (record) { return [record.status]; }) }),
        React.createElement("span", { className: styles.lastChange },
            React.createElement(FormattedDate, { value: group.lastChangedAt })))); }, [
        group.library.name,
        group.library.status,
        group.records,
        group.lastChangedAt,
    ]);
    return (React.createElement(Accordion, { id: group.library.id, open: open, onToggle: onToggle, label: accordionLabel },
        React.createElement("div", { className: styles.records }, group.records.map(function (record) { return (React.createElement(RecordRow, { key: record.id, record: record, parentOpen: open })); }))));
};
export var CrossLibraryHistoryModal = function (_a) {
    var titleId = _a.titleId, titleName = _a.titleName, open = _a.open, onClose = _a.onClose;
    var formatMessage = useIntl().formatMessage;
    var _b = useCrossLibraryHistory(titleId, open), data = _b.data, isLoading = _b.isLoading, isError = _b.isError;
    var sortedData = useMemo(function () {
        if (!data)
            return data;
        return __spreadArray([], data, true).sort(function (a, b) {
            if (a.activeLibrary === b.activeLibrary)
                return 0;
            return a.activeLibrary ? -1 : 1;
        });
    }, [data]);
    var _c = useState({}), openAccordions = _c[0], setOpenAccordions = _c[1];
    useEffect(function () {
        if (!open) {
            setOpenAccordions({});
        }
    }, [open]);
    var handleToggleAccordion = useCallback(function (_a) {
        var id = _a.id;
        setOpenAccordions(function (s) {
            var _a;
            return (__assign(__assign({}, s), (_a = {}, _a[id] = !s[id], _a)));
        });
    }, []);
    var footer = useMemo(function () { return (React.createElement(ModalFooter, null,
        React.createElement(Button, { onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.common.close" })))); }, [onClose]);
    return (React.createElement(Modal, { open: open, onClose: onClose, label: formatMessage({ id: "ui-br-legaldeposits.title.crossLibraryHistory.title" }, { title: titleName }), size: "large", dismissible: true, footer: footer },
        isLoading && React.createElement(Loading, null),
        isError && (React.createElement("div", { className: styles.error },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.common.error" }))),
        sortedData && sortedData.length === 0 && (React.createElement("div", { className: styles.empty },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.crossLibraryHistory.empty" }))),
        sortedData && sortedData.length > 0 && (React.createElement(AccordionSet, null, sortedData.map(function (group) {
            var _a;
            return (React.createElement(LibraryAccordion, { key: group.library.id, group: group, open: (_a = openAccordions[group.library.id]) !== null && _a !== void 0 ? _a : false, onToggle: handleToggleAccordion }));
        })))));
};
